/**
 * dev-product-buybox — the buy box's progressive enhancement: the gallery
 * thumbnail swap, keeping the price in step with the chosen ring size or plan,
 * and making the plan card's includes strip keyboard-reachable while it
 * overflows.
 *
 * This lives in the section's asset rather than in the blocks because a <script>
 * tag emitted from a block is a real element sitting among the section's
 * children — and those children are CSS grid items here, so a stray tag becomes
 * an invisible grid child that breaks every `+` adjacency rule after it.
 *
 * The cart does NOT depend on this file. The primary size selector emits real
 * radios named `id` carrying variant ids, so the form posts the right variant
 * with JavaScript disabled. All this does is re-render the price text, which is
 * the one thing plain HTML cannot do.
 *
 * Money strings are pre-formatted by Liquid and published per variant in a
 * `<script type="application/json" data-dev-buybox-variants>` tag next to the
 * form, including the "two bought separately" bundle figures for each allowed
 * multiplier. That way the shop's own currency format is always used and this
 * file never does arithmetic on money.
 *
 * The Complete Protocol buy box adds a second thing that moves the price: the
 * plan cards. A plan card carries the money strings it should show on its own
 * input (`data-price` / `data-compare`), already formatted by Liquid from the
 * variant it is bound to, so the handler below reads them straight off the
 * element rather than looking anything up. That matters for the two bindings
 * that are NOT a variant — a selling plan or a line item property — where the
 * variant table has nothing to say. The cart is still independent of this file:
 * a plan card bound to a variant is a real radio named `id`.
 */
(function () {
  if (window.devProductBuybox) {
    window.devProductBuybox.scan();
    return;
  }

  /** @param {Element} section */
  function readVariants(section) {
    const tag = section.querySelector('[data-dev-buybox-variants]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent || '{}');
    } catch (error) {
      console.warn('dev-product-buybox: variant table did not parse', error);
      return null;
    }
  }

  /**
   * The one place the price row is written, whatever moved it — a ring size or
   * a plan card. Both callers used to end in the same five lines, which is
   * exactly why the same bug lived in the file twice.
   *
   * THE ACCESSIBLE LABEL HAS TO SURVIVE AN EMPTY COMPARE VALUE. `textContent =`
   * destroys every child, the visually-hidden "Regular price" span included.
   * Re-attaching it only `if (value)` meant that the first time an empty string
   * came through — the one-time plan card publishes `data-compare=""` — the
   * span was detached for the rest of the session, and switching back rendered
   * a bare "$1,628.00" that a screen reader announces beside the real price
   * with nothing to say it is the struck one (`line-through` is not announced).
   * So the label goes back unconditionally; it is hidden either way, and the
   * whole element is `hidden` when there is no figure to show.
   *
   * @param {Element} priceBlock
   * @param {string} price
   * @param {string|null} compareValue  null leaves the compare figure untouched
   */
  function paintPrice(priceBlock, price, compareValue) {
    // GIFT ROWS ARE NEVER REPAINTED. A gift's wording is a fact about the offer,
    // not about the chosen variant: on the FREE-ring page the ring's variants
    // really do cost $299, so repainting from the variant table replaced "Free"
    // with $299.00 the moment a size was clicked. The variant table cannot know
    // the ring is free, so the one thing it must not do is overrule Liquid here.
    //
    // Only the AMOUNT is frozen. The compare figure still follows its own
    // source, so a gift row can keep a struck value that changes with the
    // variant if it is ever configured that way.
    const isGift = priceBlock.hasAttribute('data-price-gift');

    const current = priceBlock.querySelector('[data-dev-price-current]');
    if (current && price && !isGift) current.textContent = price;

    if (compareValue === null) return;

    const compare = priceBlock.querySelector('[data-dev-price-compare]');
    if (!compare) return;

    const label = compare.querySelector('.visually-hidden');
    compare.textContent = compareValue;
    if (label) compare.prepend(label);
    compare.hidden = !compareValue;
  }

  /**
   * @param {Element} section
   * @param {Record<string, any>} variants
   * @param {string} variantId
   */
  function renderPrice(section, variants, variantId) {
    // No size chosen yet (none is pre-selected): every size is one price, so
    // the first variant stands in for the figure.
    const data = variants[variantId] || variants[Object.keys(variants)[0]];
    if (!data) return;

    const priceBlock = section.querySelector('[data-dev-price]');
    if (!priceBlock) return;

    // "2 people" on a ring bundle (section setting pair_percent_off): two of
    // the chosen ring at the bundle price, struck against two at full price.
    if (data.pair && wantsTwo(section)) {
      paintPrice(priceBlock, data.pair, (data.bundle && data.bundle['2']) || '');
      return;
    }

    const source = priceBlock.getAttribute('data-compare-source');
    const multiplier = priceBlock.getAttribute('data-compare-multiplier') || '2';

    // A typed total-value figure has nothing to do with the chosen variant, so
    // a size change must leave it exactly where Liquid put it — `null`, not ''.
    let value = null;
    if (source === 'variant') value = data.compare || '';
    else if (source === 'bundle') value = (data.bundle && data.bundle[multiplier]) || '';
    else if (priceBlock.dataset.devCompareOriginal !== undefined) {
      // Back to one person: the struck figure Liquid printed (see wire()).
      value = priceBlock.dataset.devCompareOriginal;
    }

    paintPrice(priceBlock, data.price, value);
  }

  /**
   * Is a "How many people?" toggle on its second segment? Same reading as the
   * partner gate in blocks/dev-size-selector.liquid: the value when it is a
   * number, the position otherwise.
   *
   * @param {Element} section
   */
  function wantsTwo(section) {
    const toggles = Array.from(section.querySelectorAll('[data-dev-toggle-input]'));
    for (let i = 0; i < toggles.length; i++) {
      if (!toggles[i].checked) continue;
      const n = parseFloat(toggles[i].value);
      return Number.isNaN(n) ? i > 0 : n > 1;
    }
    return false;
  }

  /** @param {Element} section */
  function currentVariantId(section) {
    const primary = section.querySelector('input[data-dev-size-input][data-role="primary"]:checked');
    return primary ? primary.value : '';
  }

  /**
   * RING BUNDLE CODE (section setting discount_code). The BOGO and "2 people"
   * ring prices come from store discount CODES, so this theme — and only this
   * theme — puts the code on the cart as the page opens. Codes already on the
   * cart are kept: /cart/update.js `discount` replaces the list.
   *
   * @param {Element} section
   */
  async function applyDiscountCode(section) {
    const code = section.getAttribute('data-dev-discount-code');
    if (!code) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      const codes = (cart.discount_codes || []).map((d) => d.code);
      if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return;
      codes.push(code);
      await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
    } catch (error) {
      console.warn('dev-product-buybox: could not apply the offer code', error);
    }
  }

  /**
   * Only the checked plan card's selling plan is posted (blocks/dev-plan-card,
   * `data-dev-plan-companion`): every other card's companion is disabled.
   *
   * @param {Element} section
   */
  function syncPlanCompanions(section) {
    section.querySelectorAll('[data-dev-plan-card]').forEach((card) => {
      const companion = card.querySelector('[data-dev-plan-companion]');
      const radio = card.querySelector('[data-dev-plan-input]');
      if (companion && radio) companion.disabled = !radio.checked;
    });
  }

  /**
   * Paint the section's price row from strings the plan card already holds.
   * Nothing is looked up and nothing is computed — Liquid formatted both.
   *
   * @param {Element} section
   * @param {HTMLInputElement} input
   */
  function renderPlanPrice(section, input) {
    const priceBlock = section.querySelector('[data-dev-price]');
    if (!priceBlock) return;
    paintPrice(priceBlock, input.dataset.price || '', input.dataset.compare || '');
    // The suffix ("/mo") belongs to the plan, not the product: a one-time
    // purchase must not read "$229.00/mo". Only touched when the card says so.
    if ('suffix' in input.dataset) {
      const suffix = priceBlock.querySelector('.dev-product-price__suffix');
      if (suffix) {
        suffix.textContent = input.dataset.suffix;
        suffix.hidden = !input.dataset.suffix;
      }
    }
  }

  /**
   * The plan card's "What you'll get" strip is a horizontal scroller below
   * 990px — 542px of tiles inside a 356-362px card — and it holds no focusable
   * descendants at all, so without this a keyboard-only user can never reach
   * the last three tiles. WCAG 2.1.1.
   *
   * The attribute is added ONLY while the track actually overflows, which is
   * the pattern sections/dev-stat-grid.liquid and blocks/dev-offer-group.liquid
   * already established here: at 1440 the strip fits exactly (6 x 75 + 5 x 12 =
   * 510 in 512) and an unconditional tab stop would be dead weight.
   *
   * It lives in this file rather than in the block for the reason at the top:
   * a <script> emitted from a block is a real element among the section's grid
   * children and breaks every `+` adjacency rule after it.
   *
   * @param {Element} section
   */
  function wireScrollers(section) {
    const tracks = section.querySelectorAll('[data-dev-plan-items]');
    if (!tracks.length) return;

    function sync(track) {
      if (track.scrollWidth - track.clientWidth > 4) {
        track.setAttribute('tabindex', '0');
        track.setAttribute('role', 'group');
        track.setAttribute('aria-roledescription', 'carousel');
      } else {
        track.removeAttribute('tabindex');
        track.removeAttribute('role');
        track.removeAttribute('aria-roledescription');
      }
    }

    tracks.forEach((track) => {
      if (track.dataset.devPlanItemsReady === 'true') return;
      track.dataset.devPlanItemsReady = 'true';

      if (!track.getAttribute('aria-label')) {
        track.setAttribute('aria-label', track.getAttribute('data-carousel-label') || "What you'll get");
      }

      track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        // Only hijack the arrows when the track itself has focus.
        if (track.ownerDocument.activeElement !== track) return;
        const first = track.firstElementChild;
        if (!first) return;
        event.preventDefault();

        // POSITION IS ASSIGNED, AND DELIBERATELY NOT ANIMATED. Measured on
        // this very track: `scrollBy({behavior:'auto'})` moved it 16 -> 103,
        // while `scrollBy({behavior:'smooth'})` and a `scrollLeft` assignment
        // under CSS `scroll-behavior: smooth` both left it at 0 — wherever the
        // smooth-scroll animation does not run, the target position is never
        // reached at all. A keyboard affordance must not be able to fail that
        // way, so it is an instant jump of one tile. Scroll-snap then settles
        // it on a tile edge, which is what the stride is computed from.
        const stride = first.getBoundingClientRect().width + 12;
        const max = track.scrollWidth - track.clientWidth;
        const next = track.scrollLeft + (event.key === 'ArrowRight' ? stride : -stride);
        track.scrollLeft = Math.max(0, Math.min(max, next));
      });

      // Review II: "Enable horizontal scrolling". Touch and trackpads scroll
      // the track natively; a mouse has no sideways wheel, so it drags. The
      // track sits inside the plan's <label>, so the click that ends a real
      // drag is swallowed — otherwise letting go would also pick the plan.
      let drag = null;
      let suppressClick = false;
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (track.scrollWidth - track.clientWidth <= 4) return;
        drag = { x: event.clientX, left: track.scrollLeft, moved: false, id: event.pointerId };
      });
      track.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        // Released outside the row before the drag took hold: no pointerup
        // reached the track, so end it here rather than scroll on hover.
        if (!(event.buttons & 1)) { endDrag(); return; }
        const dx = event.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) < 5) return;
        if (!drag.moved) {
          drag.moved = true;
          track.setAttribute('data-dragging', '');
          track.setPointerCapture(event.pointerId);
        }
        track.scrollLeft = drag.left - dx;
      });
      const endDrag = () => {
        if (!drag) return;
        if (drag.moved) {
          suppressClick = true;
          setTimeout(() => { suppressClick = false; }, 0);
        }
        track.removeAttribute('data-dragging');
        drag = null;
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      track.addEventListener('dragstart', (event) => event.preventDefault());

      window.addEventListener('resize', () => sync(track));
      sync(track);
    });
  }

  /**
   * "How many people?" posting the cart quantity (dev-option-toggle with
   * posts_as: quantity). Each plan card publishes its money for 1-4 units in
   * `data-price-by-qty`, formatted by Liquid, so this only swaps strings: the
   * card's own figures, the strings its input hands to renderPlanPrice, and
   * then the section's price row from whichever card is checked.
   *
   * @param {Element} section
   * @param {string} qty
   */
  function applyQuantity(section, qty) {
    section.querySelectorAll('input[data-dev-plan-input][data-price-by-qty]').forEach((input) => {
      let table;
      try {
        table = JSON.parse(input.dataset.priceByQty || '{}');
      } catch (error) {
        return;
      }
      const entry = table[qty] || table['1'];
      if (!entry) return;
      input.dataset.price = entry[0] || '';
      input.dataset.compare = entry[1] || '';

      const card = input.closest('[data-dev-plan-card]');
      if (!card) return;
      const price = card.querySelector('[data-dev-plan-price]');
      if (price && entry[0]) price.textContent = entry[0];
      const compare = card.querySelector('[data-dev-plan-compare]');
      if (compare && entry[1]) {
        const label = compare.querySelector('.visually-hidden');
        compare.textContent = entry[1];
        if (label) compare.prepend(label);
      }
    });

    const checked = section.querySelector('input[data-dev-plan-input]:checked');
    if (checked) renderPlanPrice(section, checked);
  }

  /**
   * "How many people?" posting the VARIANT (dev-option-toggle with posts_as:
   * variant — 1 Person / 2 People on the Yakuru Complete Protocol). A selling
   * plan belongs to one variant and the cart refuses any other ("Cannot apply
   * selling plan to variant"), so the subscription card's `selling_plan` value
   * has to move with the toggle, and so does every card's money. Each card
   * publishes [price, compare, plan] per variant in `data-by-variant`, all
   * formatted by Liquid.
   *
   * @param {Element} section
   * @param {string} variantId
   */
  function applyVariant(section, variantId) {
    section.querySelectorAll('input[data-dev-plan-input][data-by-variant]').forEach((input) => {
      let table;
      try {
        table = JSON.parse(input.dataset.byVariant || '{}');
      } catch (error) {
        return;
      }
      const entry = table[variantId];
      if (!entry) return;
      input.value = entry[2] || '';
      input.dataset.price = entry[0] || '';
      input.dataset.compare = entry[1] || '';

      const card = input.closest('[data-dev-plan-card]');
      if (!card) return;
      const price = card.querySelector('[data-dev-plan-price]');
      if (price && entry[0]) price.textContent = entry[0];
      const compare = card.querySelector('[data-dev-plan-compare]');
      if (compare) {
        const label = compare.querySelector('.visually-hidden');
        compare.textContent = entry[1] || '';
        if (label) compare.prepend(label);
        compare.hidden = !entry[1];
      }
    });

    const checked = section.querySelector('input[data-dev-plan-input]:checked');
    if (checked) renderPlanPrice(section, checked);
  }

  /**
   * Stress test 2026-10-17: with "2 people" chosen, "What you'll get" still
   * listed one of everything. The plan cards' items follow the people count
   * read from the chosen toggle's label ("2 people"): "2× Live Supplement",
   * "$258.00 value". One person shows the strings exactly as set.
   * @param {Element} section
   */
  function applyPeople(section) {
    const toggle = section.querySelector('input[data-dev-toggle-variant]:checked');
    if (!toggle) return;
    const label = toggle.id ? section.querySelector(`label[for="${toggle.id}"]`) : null;
    const count = Math.max(1, parseInt((label ? label.textContent : '').trim(), 10) || 1);
    section.querySelectorAll('.dev-plan-card__item').forEach((item) => {
      const name = item.querySelector('.dev-plan-card__item-name');
      const value = item.querySelector('.dev-plan-card__item-value');
      if (name) {
        if (name.dataset.devOne === undefined) name.dataset.devOne = name.innerHTML;
        name.innerHTML = count > 1 ? `${count}× ${name.dataset.devOne}` : name.dataset.devOne;
      }
      if (value) {
        if (value.dataset.devOne === undefined) value.dataset.devOne = value.textContent;
        value.textContent =
          count > 1
            ? value.dataset.devOne.replace(/\$([\d,]+(?:\.\d+)?)/, (m, n) => {
                const total = parseFloat(n.replace(/,/g, '')) * count;
                return '$' + total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              })
            : value.dataset.devOne;
      }
    });
  }

  /** @param {Element} section */
  function wire(section) {
    const variants = readVariants(section);

    // A restored form (back/forward cache, or the browser's own form-state
    // restore) can open with "2 people" already checked, so the money follows
    // the checked quantity before the plan price is painted below.
    const checkedQty = section.querySelector('input[data-dev-toggle-input][name="quantity"]:checked');
    if (checkedQty) applyQuantity(section, checkedQty.value);
    const checkedVariant = section.querySelector('input[data-dev-toggle-variant]:checked');
    if (checkedVariant) applyVariant(section, checkedVariant.value);
    applyPeople(section);
    if (variants && section.hasAttribute('data-dev-pair')) {
      // Remember the one-person struck figure before "2 people" overwrites it.
      const priceBlock = section.querySelector('[data-dev-price]');
      const compare = priceBlock && priceBlock.querySelector('[data-dev-price-compare]');
      if (compare) {
        const label = compare.querySelector('.visually-hidden');
        priceBlock.dataset.devCompareOriginal = compare.textContent.replace(label ? label.textContent : '', '').trim();
      }
    }
    if (variants && section.hasAttribute('data-dev-pair') && currentVariantId(section)) {
      renderPrice(section, variants, currentVariantId(section));
    }
    applyDiscountCode(section);

    // PRICE ON LOAD follows the plan card that starts checked. Liquid paints
    // the price row from the product's selected variant, which on the
    // supplements is the ONE-TIME price ($129) while "Subscribe & Save" ($104)
    // is the card pre-selected — the page opened contradicting itself until a
    // card was clicked. The checked card's own strings win, exactly as they do
    // on change.
    const checkedPlan = section.querySelector('input[data-dev-plan-input]:checked');
    if (checkedPlan) renderPlanPrice(section, checkedPlan);
    syncPlanCompanions(section);

    section.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.dataset.devPlanInput !== undefined) {
        renderPlanPrice(section, input);
        syncPlanCompanions(section);
        return;
      }

      if (input.dataset.devToggleInput !== undefined && input.name === 'quantity') {
        applyQuantity(section, input.value);
        return;
      }

      if (input.dataset.devToggleVariant !== undefined) {
        applyVariant(section, input.value);
        applyPeople(section);
        return;
      }

      if (input.dataset.devToggleInput !== undefined) {
        if (variants && section.hasAttribute('data-dev-pair')) renderPrice(section, variants, currentVariantId(section));
        return;
      }

      // The variant table only exists when the section resolved a product; the
      // size selector is the only consumer that needs it, so the guard sits
      // here rather than at the top — a plan-card-only buy box previewed on a
      // page template must still update its price.
      if (!variants) return;
      if (input.dataset.devSizeInput === undefined || input.dataset.role !== 'primary') return;
      renderPrice(section, variants, input.value);
    });
  }

  /**
   * Thumbnail rail → main stage. Every image is already in the DOM; a swap only
   * flips `hidden`, so it never waits on the network and each image keeps its
   * own srcset. With this file absent the first image is simply the one you see.
   *
   * @param {Element} section
   */
  function wireGalleries(section) {
    section.querySelectorAll('.dev-buybox-gallery').forEach((gallery) => {
      const rail = gallery.querySelector('[data-dev-gallery-rail]');
      const stage = gallery.querySelector('[data-dev-gallery-stage]');
      if (!rail || !stage) return;

      rail.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const thumb = target.closest('[data-dev-gallery-thumb]');
        if (!thumb) return;

        const index = thumb.getAttribute('data-dev-gallery-thumb');

        stage.querySelectorAll('[data-dev-gallery-slide]').forEach((slide) => {
          slide.hidden = slide.getAttribute('data-dev-gallery-slide') !== index;
        });

        rail.querySelectorAll('[data-dev-gallery-thumb]').forEach((other) => {
          const on = other === thumb;
          other.classList.toggle('is-active', on);
          if (on) other.setAttribute('aria-current', 'true');
          else other.removeAttribute('aria-current');
        });
      });
    });
  }

  function scan() {
    document.querySelectorAll('.dev-buybox').forEach((section) => {
      if (section.dataset.buyboxReady === 'true') return;
      section.dataset.buyboxReady = 'true';
      wire(section);
      wireGalleries(section);
      wireScrollers(section);
    });
  }

  window.devProductBuybox = { scan };
  document.addEventListener('shopify:section:load', scan);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();

// Sticky gallery: publish each gallery's rendered height so the section's CSS
// can pin it bottom-aligned when it is taller than the viewport (see the note
// on `.dev-buybox-gallery` in sections/dev-product-buybox.liquid).
(() => {
  if (!('ResizeObserver' in window)) return;
  const ro = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const box = entry.target.getBoundingClientRect();
      entry.target.style.setProperty('--dev-gallery-h', `${Math.ceil(box.height)}px`);
      fitGallery(entry.target);
    }
  });
  // Width that lets the gallery fit between the header and the window's
  // bottom edge (see the note in sections/dev-product-buybox.liquid).
  // Always measured with the side padding switched off, at the column's full
  // width, so the result never feeds on itself. (The first version divided
  // the padded height by the padded width; the strip and thumbnails do not
  // shrink with the image, so every re-layout narrowed it further, down to
  // the 240px floor.) A second measurement at the estimated width corrects
  // for those fixed parts.
  function fitGallery(gallery) {
    if (gallery.dataset.devFitting) return;
    gallery.dataset.devFitting = '1';
    const headerH = parseFloat(getComputedStyle(gallery).getPropertyValue('--header-height')) || 96;
    const avail = window.innerHeight - headerH - 48;
    const prev = gallery.style.paddingInline;
    gallery.style.paddingInline = '0px';
    const full = gallery.clientWidth;
    let fit = full;
    const h0 = gallery.getBoundingClientRect().height;
    if (full > 0 && h0 > avail) {
      fit = (full * avail) / h0;
      gallery.style.paddingInline = `${Math.max(0, (full - fit) / 2)}px`;
      const h1 = gallery.getBoundingClientRect().height;
      if (h1 > 0) fit = (fit * avail) / h1;
    }
    gallery.style.paddingInline = prev;
    const value = `${Math.round(Math.min(full, Math.max(240, fit)))}px`;
    if (gallery.style.getPropertyValue('--dev-gallery-fit') !== value) gallery.style.setProperty('--dev-gallery-fit', value);
    delete gallery.dataset.devFitting;
  }
  const watch = () => document.querySelectorAll('.dev-buybox-gallery').forEach((g) => ro.observe(g));
  window.addEventListener('resize', () => document.querySelectorAll('.dev-buybox-gallery').forEach(fitGallery));
  watch();
  document.addEventListener('shopify:section:load', watch);
})();

// Loom 2026-10-19 (Daria): the two rings of a "2 people" add are one pair in
// the cart. Both lines carry `_dev_pair` = "<token>:1" / "<token>:2" (hidden
// inputs in blocks/dev-size-selector.liquid); a fresh token per add keeps two
// pairs of the same sizes apart. Registered first, so every add path below and
// Horizon's product form post the stamped values.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const fields = form.querySelectorAll('[data-dev-pair-token]');
    if (!fields.length) return;
    const token = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    fields.forEach((field) => {
      field.value = `${token}:${field.getAttribute('data-dev-pair-token')}`;
    });
  },
  true
);

// "Add to cart, then go" CTAs (blocks/dev-buybox-cta.liquid, after_click:
// add_then_go). Capture phase so it runs before Horizon's
// product-form-component, which would add by AJAX and open the cart drawer
// instead of continuing the funnel. Only submits made BY such a button are
// taken over; every other add-to-cart behaves exactly as before.
//
// On WINDOW, not document (869fb6r2w: the protocol was sometimes in the cart
// twice). Horizon's component.js registers its delegated `on:submit` listener
// on document in the capture phase too, and it loads before this deferred
// script, so it ran FIRST and AJAX-added the plan; this handler then added it
// again and navigated. Whether the first request survived the navigation was
// a race — hence "sometimes". Window capture runs before any document
// listener, and stopImmediatePropagation here keeps the event from reaching
// Horizon at all.
// "Checkout Now" (the buy box's second button, blocks/dev-buybox-cta.liquid):
// stress test 2026-10-17 — it was a second Add to Cart and opened the cart
// drawer. It adds the same item, then goes straight to checkout. Registered
// before the funnel handler below, on window in the capture phase, so neither
// that handler nor Horizon's product form also adds it. The browser has
// already checked required fields (ring size) before a submit event fires.
window.addEventListener(
  'submit',
  async (event) => {
    const button = event.submitter;
    // Daria Figma 2026-10-21 (9342:11895): the button whose job is checkout
    // says so itself (data-dev-then="checkout", blocks/dev-buybox-cta.liquid),
    // instead of "the second button" standing for it. On the Ultra Ring and
    // supplement pages that is still their second button, "Checkout Now".
    if (!button || button.getAttribute('data-dev-then') !== 'checkout') return;
    // A funnel CTA's checkout button ("Checkout Now" on the Complete Protocol)
    // still goes through the ring step: the funnel handler below owns it.
    if (button.closest('[data-dev-return-to]')) return;
    const form = /** @type {HTMLFormElement} */ (event.target);
    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    try {
      const body = new FormData(form);
      body.delete('return_to');
      const res = await fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body });
      if (!res.ok) throw new Error(`cart/add ${res.status}`);
      window.location.assign(root + 'checkout');
    } catch (error) {
      // Native post with return_to=/checkout: same result without the script.
      button.disabled = false;
      button.removeAttribute('aria-busy');
      let back = form.querySelector('input[name="return_to"]');
      if (!back) {
        back = document.createElement('input');
        back.type = 'hidden';
        back.name = 'return_to';
        form.append(back);
      }
      back.value = root + 'checkout';
      HTMLFormElement.prototype.submit.call(form);
    }
  },
  true
);

window.addEventListener(
  'submit',
  async (event) => {
    const button = event.submitter;
    const host = button && button.closest('[data-dev-return-to]');
    if (!host) return;
    const form = event.target;
    // A chosen plan card may send the customer elsewhere (869fb6r2w: Monthly
    // has no free ring, so it goes to the cart, not the free-ring page).
    const plan = form.querySelector('[data-dev-plan-input]:checked');
    const destination = (plan && plan.dataset.nextUrl) || host.getAttribute('data-dev-return-to');
    // Mitchell 2026-10-20 / Daria Figma 2026-10-21 (9342:11854, 9342:11895):
    // the Complete Protocol offers "Add to Cart" and "Checkout Now", like the
    // Ultra Ring page. Both go through the ring-size popup (subscription);
    // Checkout Now (data-dev-then="checkout") then goes to checkout, Add to
    // Cart opens the cart drawer. A funnel CTA with one button keeps ending in
    // the cart drawer.
    const thenCheckout = button.getAttribute('data-dev-then') === 'checkout';
    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const body = new FormData(form);
      body.delete('return_to');
      // QA video (Pricing and subscription logic): starting the funnel again
      // stacked a second "1 Person" protocol on the one already in the cart,
      // so the FREE-ring page counted 2 people and asked for two rings. The
      // funnel sells ONE plan: the plan from an earlier visit, and the free
      // rings that came with it, make way for the one chosen now.
      // A roll-back still running from a popup just closed finishes first,
      // or it would put its plan back next to this one.
      if (window.devFunnelOverlay && window.devFunnelOverlay.settled) await window.devFunnelOverlay.settled();
      const replaced = await replaceEarlierPlan(root, body.get('product-id'));
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      if (!res.ok) throw new Error(`cart/add ${res.status}`);
      const planAdded = await res.clone().json().catch(() => null);
      // A plan whose next stop is the cart (One-time Purchase, no free ring)
      // opens the cart drawer on this page, like the end of the free-ring
      // step, instead of loading the bare /cart page.
      const toCart = new URL(destination, window.location.href).pathname.replace(/\/$/, '') === root.replace(/\/$/, '') + '/cart';
      if (toCart && thenCheckout) {
        window.location.assign(root + 'checkout');
        return;
      }
      if (toCart && window.devFunnelOverlay && window.devFunnelOverlay.announceAdd) {
        const added = await res.json();
        const items = added.items || [added];
        await window.devFunnelOverlay.announceAdd(
          items.map((item) => ({ merchandiseId: `gid://shopify/ProductVariant/${item.variant_id || item.id}`, quantity: item.quantity || 1 }))
        );
        button.disabled = false;
        button.removeAttribute('aria-busy');
        return;
      }
      // Review note (869 round 7): the FREE-ring step works as an overlay on
      // the page that sent the customer there. A plan with its own next URL
      // (Monthly → cart) still navigates.
      if (host.hasAttribute('data-dev-overlay') && !(plan && plan.dataset.nextUrl) && window.devFunnelOverlay) {
        try {
          // Stress test 2026-10-17: closing the ring-size step left the plan
          // in the cart with no ring, bottle or membership. The overlay now
          // takes the plan back out unless its own add goes through.
          const planKeys = ((planAdded && (planAdded.items || [planAdded])) || []).map((item) => item.key).filter(Boolean);
          await window.devFunnelOverlay.open(destination, {
            planKeys,
            restore: replaced,
            returnFocus: button,
            then: thenCheckout ? 'checkout' : 'cart',
          });
          button.disabled = false;
          button.removeAttribute('aria-busy');
          return;
        } catch (overlayError) {
          // Could not build the overlay: the page itself still works.
        }
      }
      window.location.assign(destination);
    } catch (error) {
      // Fall back to the native post, which carries return_to and still
      // ends up on the destination with the product in the cart.
      button.disabled = false;
      button.removeAttribute('aria-busy');
      HTMLFormElement.prototype.submit.call(form);
    }
  },
  true
);

// Removes the cart lines of `productId` and the free ($0) gift lines before a
// funnel CTA adds its plan. Best effort: a failed read or update leaves the
// cart as it is and the add still goes ahead. Returns what it took out, as
// /cart/add.js items, so leaving the ring step can put it back (retest
// 2026-10-19: Start Now with a plan already in the cart, then closing the
// popup, left the cart empty).
async function replaceEarlierPlan(root, productId) {
  if (!productId) return [];
  try {
    const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    const updates = {};
    const removed = [];
    for (const item of cart.items || []) {
      const props = item.properties && !Array.isArray(item.properties) ? item.properties : {};
      // A ring pair's second ring can be $0 (BOGO): it is not a plan gift.
      if (props._dev_pair) continue;
      const isPlan = String(item.product_id) === String(productId);
      const isGift = (item.final_line_price === 0 && item.original_line_price > 0) || Boolean(props._dev_gift);
      if (!isPlan && !isGift) continue;
      updates[item.key] = 0;
      const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      removed.push({
        id: item.variant_id,
        quantity: item.quantity,
        ...(Object.keys(props).length ? { properties: props } : {}),
        ...(plan ? { selling_plan: plan.id } : {}),
      });
    }
    if (!removed.length) return [];
    const res = await fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return res.ok ? removed : [];
  } catch (e) {
    // Leave the cart alone.
    return [];
  }
}

// A required size left unchosen: the browser cancels the submit and would show
// its "please select" bubble on the radio, but these radios are visually
// custom, so nothing appeared and the button looked dead (BOGO partner size).
// Show the selector's own message and bring it into view instead.
document.addEventListener(
  'invalid',
  (event) => {
    const selector = event.target.closest && event.target.closest('.dev-size-selector');
    if (!selector) return;
    event.preventDefault();
    if (selector.classList.contains('is-invalid')) return;
    selector.classList.add('is-invalid');
    const msg = selector.querySelector('.dev-size-selector__error');
    if (msg) msg.hidden = false;
    selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
  },
  true
);
document.addEventListener('change', (event) => {
  const selector = event.target.closest && event.target.closest('.dev-size-selector.is-invalid');
  if (!selector) return;
  selector.classList.remove('is-invalid');
  const msg = selector.querySelector('.dev-size-selector__error');
  if (msg) msg.hidden = true;
});


// Funnel overlay (review note on the FREE-ring page: "This page should work as
// an overlay, so when we click the button, the overlay closes, the Complete
// Protocol page appears again, and the cart opens with the CP added to it").
//
// The destination page is fetched and its <main> sections are moved into a
// full-screen modal <dialog>. All section CSS is compiled into one global
// stylesheet, so it is already on this page; inline scripts (size selectors,
// gallery) are re-created so they run; buy boxes are re-scanned. The add made
// inside the overlay goes to /cart/add.js, the overlay closes, and the
// standard CartLinesUpdateEvent tells the cart drawer (and the header count)
// to refresh and open — the same path every other add on the site takes.
(() => {
  if (window.devFunnelOverlay) return;
  const root = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
  /** @type {HTMLDialogElement | null} */
  let dialog = null;
  /** Cart keys of the plan the funnel added before this step; removed again if the step is left. */
  let pendingPlanKeys = [];
  /** Lines the funnel took out to make way for this plan; put back if the step is left. */
  let pendingRestore = [];
  /** The last roll-back, so a new Start Now waits for it before reading the cart. */
  let lastRollBack = Promise.resolve();
  /** @type {HTMLElement | null} */
  let returnFocus = null;
  /** Where the step leads once the ring is added: 'cart' (drawer) or 'checkout'. */
  let then = 'cart';

  function runScripts(scope) {
    scope.querySelectorAll('script').forEach((old) => {
      const type = (old.getAttribute('type') || '').trim();
      if (type && type !== 'text/javascript' && type !== 'module') return; // JSON data stays as is
      if (old.src) {
        // External scripts the page needs are already loaded on this page.
        old.remove();
        return;
      }
      const fresh = document.createElement('script');
      if (type) fresh.type = type;
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  }

  /** @param {{ claimed?: boolean }} [options] */
  function close({ claimed = false } = {}) {
    if (!dialog) return;
    const d = dialog;
    dialog = null;
    if (d.open) d.close();
    d.remove();
    document.documentElement.classList.remove('dev-funnel-overlay-open');
    const keys = pendingPlanKeys;
    const restore = pendingRestore;
    pendingPlanKeys = [];
    pendingRestore = [];
    // Back to the button that opened the step (keyboard / screen readers).
    if (returnFocus && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
    if (!claimed && keys.length) lastRollBack = rollBack(keys, restore);
  }

  /**
   * The step was left without a ring: the plan it was opened for comes out
   * again, and a plan it replaced (with its free items) goes back in.
   * @param {string[]} keys @param {object[]} [restore]
   */
  async function rollBack(keys, restore = []) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: keys.map((id) => ({ id, quantity: 0 })), promise: deferred.promise })
      );
      const updates = Object.fromEntries(keys.map((key) => [key, 0]));
      const res = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      let cart = await res.json();
      if (restore.length) {
        await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: restore }),
        });
        cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'dev-funnel-overlay', didError: false },
      });
    } catch (error) {
      // The cart drawer still shows the plan, and asks for the ring size.
    }
  }

  /** @param {string} url @param {{ planKeys?: string[], restore?: object[], returnFocus?: HTMLElement, then?: string }} [options] */
  async function open(url, options = {}) {
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`overlay ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const main = doc.querySelector('main');
    if (!main || !main.querySelector('form')) throw new Error('overlay: nothing to show');

    close({ claimed: true });
    pendingPlanKeys = options.planKeys || [];
    pendingRestore = options.restore || [];
    returnFocus = options.returnFocus || null;
    then = options.then === 'checkout' ? 'checkout' : 'cart';
    const d = document.createElement('dialog');
    d.className = 'dev-funnel-overlay';
    const heading = main.querySelector('h1');
    d.setAttribute('aria-label', heading ? heading.textContent.trim() : 'Next step');

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'dev-funnel-overlay__close';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.innerHTML =
      '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    closeButton.addEventListener('click', () => close());

    const body = document.createElement('div');
    body.className = 'dev-funnel-overlay__body';
    Array.from(main.children).forEach((child) => body.append(document.adoptNode(child)));

    d.append(closeButton, body);
    d.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    // Daria Figma 2026-10-21 (8813:44287): a click on the dimmed page leaves
    // the step like Escape does — with the roll-back and the focus return.
    // The card fills the <dialog>, so only a backdrop click lands on the
    // dialog itself outside its box.
    d.addEventListener('click', (event) => {
      if (event.target !== d) return;
      const r = d.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) close();
    });
    // Anything else that closes the <dialog> natively (a theme-wide light
    // dismiss, the browser) still goes through close(), so the cart is never
    // left holding a plan without its ring.
    d.addEventListener('close', () => {
      if (dialog === d) close();
    });
    document.body.append(d);
    dialog = d;
    runScripts(body);
    if (window.devProductBuybox) window.devProductBuybox.scan();
    document.documentElement.classList.add('dev-funnel-overlay-open');
    d.showModal();
    d.scrollTop = 0;
  }

  async function announceAdd(lines) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines, promise: deferred.promise }));
      const cart = await (await fetch(`${root()}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'dev-funnel-overlay', didError: false },
      });
    } catch (error) {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.refresh === 'function') {
        await drawer.refresh();
        drawer.open();
      } else {
        window.location.assign(`${root()}cart`);
      }
    }
  }

  // The add made inside the overlay. Window capture, like the add-then-go
  // handler above, so Horizon's product form does not also add it.
  window.addEventListener(
    'submit',
    async (event) => {
      const form = /** @type {HTMLFormElement} */ (event.target);
      if (!dialog || !dialog.contains(form)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const button = /** @type {HTMLButtonElement | null} */ (event.submitter);
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      try {
        const body = new FormData(form);
        body.delete('return_to');
        // The free rings are marked, so a ring left behind without its
        // subscription is taken out by the cart instead of costing $349.
        if (body.get('id')) body.set('properties[_dev_ring]', '1');
        if (body.get('items[1][id]')) body.set('items[1][properties][_dev_ring]', '1');
        const res = await fetch(`${root()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
        if (!res.ok) throw new Error(`cart/add ${res.status}`);
        const added = await res.json();
        const items = added.items || [added];
        if (then === 'checkout') {
          // The plan and its rings are in: straight on to checkout. The popup
          // stays up, busy, until the browser leaves the page.
          pendingPlanKeys = [];
          pendingRestore = [];
          window.location.assign(`${root()}checkout`);
          return;
        }
        close({ claimed: true });
        await announceAdd(
          items.map((item) => ({ merchandiseId: `gid://shopify/ProductVariant/${item.variant_id || item.id}`, quantity: item.quantity || 1 }))
        );
      } catch (error) {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
        HTMLFormElement.prototype.submit.call(form);
      }
    },
    true
  );

  // Back from checkout: the browser can restore this page from its cache
  // exactly as it was left, popup up and buttons busy. Put it back to rest.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    if (dialog) close({ claimed: true });
    document.querySelectorAll('.dev-buybox-cta__button[aria-busy], .dev-buybox button[aria-busy]').forEach((b) => {
      b.disabled = false;
      b.removeAttribute('aria-busy');
    });
  });

  window.devFunnelOverlay = { open, close, announceAdd, settled: () => lastRollBack };
})();
